import { Expose } from 'class-transformer';
import { PccAuditInfo } from './audit-info.typings';

export type PccNutritionOrderStatus = 'active' | 'pending confirmation' | 'on hold';

export class PccNutritionOrderParams {
	statuses?: PccNutritionOrderStatus[];

	startDateTime?: string;

	endDateTime?: string;
}

/**
 * Represents required texture modifications for oral diet
 */
export class PccOralDietTexture {
	/**
	 * Description indicating how to alter the texture of the foods, e.g. pureed
	 */
	modifier: string;
}

/**
 * Represents oral diet components
 */
export class PccOralDiet {
	/**
	 * Free text directions entered for the Diet Order. Directions can be added at the time of entering an order or as an update after the order has been promoted to the patient's chart.
	 */
	additionalDirections?: string;

	/**
	 * The last valid date and time of the nutrition order. It may be empty if the medication has infinite duration. Date and time are expressed in UTC.
	 */
	endDateTime?: string;

	/**
	 * The required consistency of fluids and liquids provided to the patient
	 */
	fluidConsistencyType: string;

	/**
	 * The earliest date and time scheduled for the nutrition order. Date and time are expressed in UTC.
	 */
	startDateTime: string;

	/**
	 * Required texture modifications
	 */
	texture: PccOralDietTexture;

	/**
	 * Represent the type of diet that the patient is on, eg. Gluten, vegetarian, etc.
	 */
	type: string;
}

/**
 * Represents a nutrition order for a patient in PointClickCare
 *
 * SimpliScreen maps the nutrition order to the following string for embedding in the RAG:
 * type: {oralDiet.type}, fluidConsistencyType: {oralDiet.fluidConsistencyType}, texture: {oralDiet.texture.modifier}, additionalDirections: {oralDiet.additionalDirections}, startDateTime: {oralDiet.startDateTime}, endDateTime: {oralDiet.endDateTime}, status: {status}, orderDateTime: {orderDateTime}, orderedBy: {orderedBy}
 *
 * @see https://connect2.pointclickcare.com/api/public/preview1/orgs/{orgUuid}/nutrition-orders?patientId={patientId}
 */
export class PccNutritionOrder extends PccAuditInfo {
	/**
	 * PointClickCare's unique identifier for a Nutrition Order
	 */
	nutritionOrderId: number;

	/**
	 * Oral diet components
	 */
	oralDiet: PccOralDiet;

	/**
	 * Represents the date/time when the nutrition order was ordered. The date and time should be expressed in UTC in the following format: YYYY-MM-DDTHH:MM:SS.sssZ.
	 */
	orderDateTime: string;

	/**
	 * Represents the PointClickCare user who ordered the nutrition order. This is typically the practitioner or medical professional assigned to the patient.
	 */
	orderedBy: string;

	/**
	 * Unique identifier for the practitioner or medical professional that ordered the nutrition order
	 */
	orderedById: number;

	/**
	 * PointClickCare's unique identifier for a patient within the organization.
	 *
	 * When a patient is admitted to a facility within an organization, they are assigned a patientId which is unique to their stay within that facility. If the same patient were to get admitted to another facility within the same organization in the future, they would be assigned a new patientId. The patientId however is unique at the organization level. That is, no two patients across different facilities within the organization will have the same patientId.
	 *
	 * This identifier is crucial for tracking patient information and care but must not be displayed in any user interface due to legal privacy requirements.
	 */
	patientId: number;

	/**
	 * Represents the status of the Nutrition order for the patient. Possible values are "active", "pending confirmation", and "on hold".
	 */
	status: PccNutritionOrderStatus;

	/**
	 * Represents the details of the nutrition order.
	 *
	 * SimpliScreen maps the nutrition order to the following string for embedding in the RAG:
	 * type: {oralDiet.type}, fluidConsistencyType: {oralDiet.fluidConsistencyType}, texture: {oralDiet.texture.modifier}, additionalDirections: {oralDiet.additionalDirections}, startDateTime: {oralDiet.startDateTime}, endDateTime: {oralDiet.endDateTime}, status: {status}, orderDateTime: {orderDateTime}, orderedBy: {orderedBy}
	 */
	details: string;

	constructor(partial?: Partial<PccNutritionOrder>) {
		super();
		Object.assign(this, partial);
	}

	@Expose({ name: 'details' })
	getDetails() {
		const details = [`Data Type: Nutrition Order`];
		if (this.nutritionOrderId) details.push(`Nutrition Order ID: ${this.nutritionOrderId}`);
		if (this.oralDiet?.type) details.push(`Type: ${this.oralDiet.type}`);
		if (this.oralDiet?.fluidConsistencyType) details.push(`Fluid Consistency Type: ${this.oralDiet.fluidConsistencyType}`);
		if (this.oralDiet?.texture?.modifier) details.push(`Texture: ${this.oralDiet.texture.modifier}`);
		if (this.oralDiet?.additionalDirections) details.push(`Additional Directions: ${this.oralDiet.additionalDirections}`);
		if (this.oralDiet?.startDateTime) details.push(`Start DateTime: ${this.oralDiet.startDateTime}`);
		if (this.oralDiet?.endDateTime) details.push(`End DateTime: ${this.oralDiet.endDateTime}`);
		if (this.status) details.push(`Status: ${this.status}`);
		if (this.orderDateTime) details.push(`Order DateTime: ${this.orderDateTime}`);
		if (this.orderedBy) details.push(`Ordered By: ${this.orderedBy}`);
		return details.join(', ');
	}
}
